import { Box } from "@mui/system"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"
import { APPLICATION_STATUS, CONSULT_STATUS, MEMBER_TYPE_STRING_CAPITAL } from "src/modules/const"
import { Event } from "src/openapi"
import { EventItem } from "src/pages/event/components/EventItem"
import { Grid, Typography } from "src/UILibrary"
import { ApplicationStatusLabel } from "./ApplicationStatusLabel"
import { ApplicationTableItem } from "./ApplicationTableItem"
import { EventEditPage } from "./EventEditPage"

type Props = {
  item: any
  event: Event
  closeDetail: any
}

export const EventUserDetailPage = ({ item, event, closeDetail }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.event" })

  const isPC = useMediaQuery({ query: "(min-width: 1400px)" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const [openDetail, setOpenDetail] = useState<boolean>(false)

  let headers: any[] = [
    {
      size: isPC ? 2 : isTablet ? 2 : 1,
      name: t("date"),
      sortby: "",
    },
    {
      size: isPC ? 2 : isTablet ? 3 : 1,
      name: t("status"),
      sortby: "",
    },
    {
      size: isPC ? 2 : isTablet ? 3 : 1,
      name: t("member_name"),
      sortby: "",
    },
    {
      size: isPC ? 6 : isTablet ? 3 : 1,
      name: t("join_method"),
      sortby: "",
    },
  ]

  if (openDetail) {
    return <EventEditPage item={event} closeDetail={() => setOpenDetail(false)} />
  }

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Typography.FileFieldDetail
        sx={{
          fontFamily: "Noto Sans JP !important",
          my: "12px",
          ml: "20px",
          ":hover": { opacity: 0.8, cursor: "pointer" },
        }}
        onClick={() => closeDetail()}
      >
        {"＜　"}
        {t("page_back")}
      </Typography.FileFieldDetail>
      <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
        {headers.map((header, index) => (
          <TableHeaderItem
            key={index}
            name={header.name}
            size={header.size}
            sortby={header.sortby}
          />
        ))}
      </Grid>
      <ApplicationTableItem item={item} handleSelected={() => {}} />
      <Box sx={{ px: "20px", pt: "20px", display: "flex" }}>
        <Typography.DetailText
          sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "20px" }}
        >
          {t("status")}
        </Typography.DetailText>
        {item.application.status === APPLICATION_STATUS.RESERVED && (
          <>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.application.createdAt?.slice(0, 10)}
            </Typography.DetailText>
            <ApplicationStatusLabel
              isBehalf={item.application.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR}
              status={item.application.status as string}
            />
          </>
        )}
        {item.application.status === CONSULT_STATUS.CANCELED && (
          <>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.application.createdAt?.slice(0, 10)}
            </Typography.DetailText>
            <ApplicationStatusLabel
              isBehalf={item.application.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR}
              status={APPLICATION_STATUS.RESERVED}
            />
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", color: "#D4D3D7", mx: "10px" }}
            >
              {">"}
            </Typography.DetailText>
            <>
              <Typography.DetailText
                sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
              >
                {item.application.updatedAt?.slice(0, 10)}
              </Typography.DetailText>
              <ApplicationStatusLabel
                isBehalf={item.application.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR}
                status={item.application.status as string}
              />
            </>
          </>
        )}
      </Box>
      {item.application.status === APPLICATION_STATUS.CANCELLED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("event_is_cancelled")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "400",
              mt: "20px",
            }}
          >
            {item.application.rejectReason}
          </Typography.InputFieldTitle>
        </Box>
      )}
      <Typography.InputFieldTitle
        sx={{
          fontFamily: "Noto Sans JP !important",
          fontWeight: "700",
          pb: "10px",
          ml: "20px",
          mt: "30px",
          borderBottom: "1px dashed #D4D3D7",
          width: "100%",
        }}
      >
        {t("event_information")}
      </Typography.InputFieldTitle>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          pl: "20px",
          py: "10px",
          mt: "20px",
        }}
      >
        <EventItem item={event} handleSelected={() => setOpenDetail(true)} clickable />
      </Box>
      <Box sx={{ mt: "20px", p: "20px" }}>
        <Typography.InputFieldTitle
          sx={{
            fontFamily: "Noto Sans JP !important",
            fontWeight: "700",
            pb: "10px",
            borderBottom: "1px dashed #D4D3D7",
            width: "100%",
          }}
        >
          {t("application_user_info")}
        </Typography.InputFieldTitle>
        <Typography.InputFieldTitle
          sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
        >
          {`${item.user.lastName} ${item.user.firstName}`}
        </Typography.InputFieldTitle>
        <Typography.DetailText
          sx={{
            fontFamily: "Noto Sans JP !important",
            mt: "12px",
          }}
        >
          {item.user.email}
        </Typography.DetailText>
      </Box>
    </Box>
  )
}
